import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { BatchSpanProcessor, NoopSpanProcessor, SpanProcessor } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';

/**
 * OpenTelemetry Tracing Configuration
 * 
 * This module initializes distributed tracing for the frontend application.
 * It gracefully degrades when no OTLP collector is available (e.g., in local development).
 * 
 * PERFORMANCE NOTE:
 * Initialization is deferred to avoid blocking the main thread during initial load.
 * 
 * @see https://opentelemetry.io/docs/instrumentation/js/getting-started/browser/
 */

// Environment-driven configuration with sensible defaults
const OTLP_ENDPOINT = import.meta.env.VITE_OTEL_COLLECTOR_URL as string | undefined;
const IS_PRODUCTION = import.meta.env.PROD;

let initialized = false;

/**
 * Creates the appropriate span processor based on environment configuration.
 * Uses NoopSpanProcessor when OTLP endpoint is not configured to prevent connection errors.
 */
function createSpanProcessor(): SpanProcessor {
  // If no OTLP endpoint is configured, use a no-op processor to avoid connection errors
  if (!OTLP_ENDPOINT) {
    if (!IS_PRODUCTION) {
      console.debug('📡 OpenTelemetry: No OTLP_ENDPOINT configured, tracing disabled');
    }
    return new NoopSpanProcessor();
  }

  try {
    const exporter = new OTLPTraceExporter({
      url: OTLP_ENDPOINT,
    });
    return new BatchSpanProcessor(exporter);
  } catch (error) {
    console.warn('📡 OpenTelemetry: Failed to initialize exporter, tracing disabled', error);
    return new NoopSpanProcessor();
  }
}

/**
 * Initializes OpenTelemetry tracing.
 * Should be called after the application has rendered to avoid blocking the main thread.
 * 
 * @returns Promise that resolves when tracing is initialized
 */
export async function initializeTracing(): Promise<void> {
  if (initialized) return;

  try {
    // Check if we strictly need to wait for idle callback or just run logic
    // We'll wrap in requestIdleCallback for performance if available
    const initLogic = () => {
      const spanProcessor = createSpanProcessor();

      const provider = new WebTracerProvider({
        spanProcessors: [spanProcessor],
      });

      provider.register();

      registerInstrumentations({
        tracerProvider: provider,
        instrumentations: [getWebAutoInstrumentations()],
      });

      initialized = true;
      console.log('📡 OpenTelemetry initialized (deferred)');
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(initLogic);
    } else {
      setTimeout(initLogic, 0);
    }

  } catch (error) {
    console.error('Failed to initialize OpenTelemetry', error);
  }
}
